import React, { useCallback } from "react";
import { FieldArray, Formik, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import dayjs from "dayjs";

import Common from "us.common";
import {
  NonProductionDayDrawerType,
  NonProductionDayTypeInterface,
  NonProductionDayTypeTranslationKey,
  RedDayCriteriaType,
} from "us.collection.admin/constants";
import { nonProductionDays_New } from "us.collection.admin/actions";
import {
  RedDayCriteria,
  NonProductionDay,
} from "us.collection.admin/interfaces";
import { getRedDayCriteria } from "us.collection.admin/functions";
import { AddOrUpdateNonExecutionDay } from "./Repository";
import { ValidationSchema } from "./Validation";
import { setAllCriteriaTrue } from "./Functions";
import { $Checkbox } from "us.common/components";
import { RootState } from "us.helper/types";

const { $Button, $Switch, $Form, $RangePicker, $TextArea } = Common.Components;

const { redDays } = nonProductionDays_New;

type NonExecutionDayProps = {
  type: NonProductionDayDrawerType;
  selectedCriteria: NonProductionDayTypeInterface;
  values: NonProductionDay;
  onClose: (open: boolean) => void;
};

/**
 * @description Non Execution Day Component to view, add and edit non execution days
 * @returns JSX.Element
 * @example <NonExecutionDay />
 * @link Design document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3049914453/Non+Email+Days+UI+Design
 * @author Rajitha Sanjayamal <rajithasa@unicorn-solutions.com>
 * @since 21/07/2022
 */
const NonExecutionDay: React.FC<NonExecutionDayProps & PropsFromRedux> = (
  props,
) => {
  const { t } = useTranslation();
  const {
    type,
    selectedCriteria,
    values: drawerData,
    currentDateFormat,
    onClose,
    addOrUpdateRedDay,
  } = props;
  const { values } = useFormikContext<any>();
  const isEditable = type !== NonProductionDayDrawerType.VIEW;

  const allCriteriaChecked = useCallback((criteria: RedDayCriteria[]) => {
    return criteria?.every((item: RedDayCriteria) => item.value);
  }, []);

  /**
   * @function
   * @description save or update red days
   * @param {any} data
   */
  const onSubmit = (data: any) => {
    const requestBody = AddOrUpdateNonExecutionDay.call(data);
    addOrUpdateRedDay && addOrUpdateRedDay(requestBody, selectedCriteria);
    onClose(false);
  };

  /**
   * @function
   * @description handle all switch change event
   * @param {boolean} value
   * @param {Array<RedDayCriteria>} criterias
   * @param {(field: string, value: any, shouldValidate?: boolean) => void} setFieldValue
   */
  const onChange = (
    value: boolean,
    criterias: Array<RedDayCriteria>,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean,
    ) => void,
  ) => {
    let data: Array<RedDayCriteria> = [];
    try {
      if (value) {
        data = setAllCriteriaTrue(criterias);
      } else {
        data = getRedDayCriteria(selectedCriteria);
      }
      setFieldValue("criteria", data);
      setFieldValue("allSwitch", value);
    } catch (error) {}
  };

  /**
   * @function
   * @description disable dates
   * @param {any} current currentDate
   */
  const disabledDate = (current: any) => {
    return current.isBefore(dayjs()) || current.year() != values?.selectedYear;
  };

  /**
   * @function
   * @description get translation key for relevant criteria
   * @param {NonProductionDayTypeInterface} criteria selected criteria
   * @returns {string} Translation Key
   */
  const getTranslationKey = (criteria: string): string => {
    try {
      switch (criteria) {
        case RedDayCriteriaType.Non_SMS:
          return NonProductionDayTypeTranslationKey.Non_SMS;
        case RedDayCriteriaType.Non_Email:
          return NonProductionDayTypeTranslationKey.Non_Email;
        case RedDayCriteriaType.Non_EHF:
          return NonProductionDayTypeTranslationKey.Non_EHF;
        case RedDayCriteriaType.Non_Printing:
          return NonProductionDayTypeTranslationKey.Non_Printing;
        default:
          return "";
      }
    } catch (error) {
      return "";
    }
  };

  return (
    <Formik
      initialValues={{
        ...drawerData,
        allSwitch: allCriteriaChecked(drawerData.criteria || []),
      }}
      enableReinitialize
      validateOnMount
      validateOnChange
      validateOnBlur
      validationSchema={ValidationSchema}
      onSubmit={onSubmit}
    >
      {({ values, handleSubmit, setFieldValue, isValid, dirty }: any) => (
        <>
          <$Form>
            <div className="mb-3">
              <$TextArea
                name="reason"
                label={t("US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.REASON")}
                size="small"
                autoSize={{ minRows: 3, maxRows: 3 }}
                required
                maxLength={256}
                dataTestid="addNonExecution-reason"
                disabled={!isEditable}
              />
            </div>

            <div className="mb-3 d-flex align-items-center">
              <div className="mr-3">
                <$Switch
                  name="isRecurring"
                  checked={values.isRecurring}
                  onChange={(val: boolean) => setFieldValue("isRecurring", val)}
                  disabled={!isEditable}
                />
              </div>
              <div>
                {t(
                  "US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.RECURRING_ANNUALLY",
                )}
              </div>
            </div>

            <div className="inline-calendar">
              <$RangePicker
                label={t(
                  "US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.SELECT_DATES",
                )}
                name="dateRange"
                currentTimeFormat={currentDateFormat}
                className="non-production-range-picker"
                size="small"
                picker="date"
                disabled={!isEditable}
                disabledDate={disabledDate}
                onCalendarChange={(val: any) => {
                  setFieldValue("dateRange", val);
                }}
              />
            </div>
            <div className="mt-5 d-flex flex-1 align-items-top">
              <div className="mr-3">
                <$Switch
                  name="allSwitch"
                  checked={values.allSwitch}
                  onChange={(val: boolean) =>
                    onChange(val, values.criteria, setFieldValue)
                  }
                  disabled={!isEditable}
                />
              </div>
              <div>{t("US.COMMON:COMMON.ALL")}</div>
            </div>
            <FieldArray
              name="criteria"
              render={() => (
                <>
                  {values.criteria?.length > 0 &&
                    values.criteria?.map(
                      (criteria: RedDayCriteria, index: number) => (
                        <div
                          className="mt-1 d-flex flex-1 align-items-top"
                          key={index}
                        >
                          <div className="mr-3">
                            <$Checkbox
                              name={criteria.type}
                              checked={criteria.value}
                              onChange={(e: any) => {
                                const nextCriteria = values.criteria?.map(
                                  (item: RedDayCriteria, i: number) =>
                                    i === index
                                      ? { ...item, value: e.target.checked }
                                      : item,
                                );
                                const allChecked =
                                  allCriteriaChecked(nextCriteria);
                                setFieldValue("allSwitch", allChecked);
                                setFieldValue("criteria", nextCriteria);
                              }}
                              disabled={
                                !isEditable ||
                                criteria.type === selectedCriteria
                              }
                            />
                          </div>
                          <div>
                            {t(
                              `US.COLLECTION.ADMIN:NON_PRODUCTION_DAYS.${getTranslationKey(
                                criteria.type,
                              )}`,
                            )}
                          </div>
                        </div>
                      ),
                    )}
                </>
              )}
            />
            <div className="drawer-footer-fixed align-content-center justify-content-end">
              <div>
                <$Button
                  type="primary"
                  disabled={!isEditable || !isValid || !dirty}
                  onClick={handleSubmit}
                >
                  {type === NonProductionDayDrawerType.EDIT
                    ? t("US.COMMON:COMMON.UPDATE")
                    : t("US.COLLECTION.COMMON:COMMON.ADD_TO_LIST")}
                </$Button>
                <$Button onClick={() => onClose(false)}>
                  {t("US.COMMON:COMMON.CANCEL")}
                </$Button>
              </div>
            </div>
          </$Form>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { currentDateFormat } = state.common;
  return {
    currentDateFormat,
  };
};

const mapDispatchToProps = {
  addOrUpdateRedDay: redDays.saveOrUpdate,
  deleteRedDay: redDays.remove,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(NonExecutionDay);
